import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert, Wallet } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { salaryAssignmentsApi, salaryStructuresApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { SalaryAssignment } from '@/types/hr';
import { formatDate, orDash } from '@/utils/format';
import { Badge, Money, today } from './shared';
import { EmployeePicker, type PickedEmployee } from './EmployeePicker';

/**
 * What one employee is paid, and what they were paid before.
 *
 * <p>Salary is a dated series rather than a field: a raise closes the current row
 * and opens a new one, so a payslip from last March can still be explained by the
 * row that was in force when it was produced. The history is the point of the
 * screen, not a footnote to it.
 */
export function SalaryAssignmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.SALARY_ASSIGNMENT_MANAGE);

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [structureId, setStructureId] = useState('');
  const [basic, setBasic] = useState('');
  const [from, setFrom] = useState(today());
  const [reason, setReason] = useState('');

  const structures = useQuery({
    queryKey: queryKeys.salaryStructures.list(true),
    queryFn: () => salaryStructuresApi.list(true),
  });

  const history = useQuery({
    queryKey: queryKeys.salaryAssignments.history(employee?.id ?? ''),
    queryFn: () => salaryAssignmentsApi.history(employee!.id),
    enabled: employee !== null,
  });

  const current = useQuery({
    queryKey: queryKeys.salaryAssignments.current(employee?.id ?? ''),
    queryFn: () => salaryAssignmentsApi.current(employee!.id),
    enabled: employee !== null,
    // A person with no package is a normal state, not an error to shout about.
    retry: false,
  });

  const assign = useMutation({
    mutationFn: () =>
      salaryAssignmentsApi.assign({
        employeeId: employee!.id,
        structureId,
        basicSalary: Number(basic),
        effectiveFrom: from,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    onSuccess: (assignment) => {
      toast.success(
        assignment.rangeWarning ?? `${employee?.name} placed on ${assignment.structureName}`,
      );
      setAssigning(false);
      setBasic('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.salaryAssignments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.dashboard });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<SalaryAssignment>[] = [
    {
      key: 'period',
      header: 'In force',
      render: (row) => (
        <span className="text-sm text-ink">
          {formatDate(row.effectiveFrom)} →{' '}
          {row.effectiveTo === undefined ? (
            <Badge tone="success">current</Badge>
          ) : (
            formatDate(row.effectiveTo)
          )}
        </span>
      ),
    },
    {
      key: 'structure',
      header: 'Package',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.structureName)}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.structureCode)}
          </span>
        </span>
      ),
    },
    {
      key: 'basic',
      header: 'Basic',
      className: 'text-right',
      render: (row) => <Money value={row.basicSalary} bold={row.current} />,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => <span className="text-[13px] text-muted">{orDash(row.reason)}</span>,
    },
  ];

  const breakdown = current.data?.breakdown;

  return (
    <div>
      <PageHeader
        title="Staff Salaries"
        description="What somebody is paid today, and every figure they were on before."
        icon={<Wallet className="h-5 w-5" aria-hidden="true" />}
        action={
          manages && employee !== null ? (
            <Button onClick={() => setAssigning(true)}>
              {current.data === undefined ? 'Set salary' : 'Record a raise'}
            </Button>
          ) : undefined
        }
      />

      <Card className="p-4">
        <div className="max-w-sm">
          <EmployeePicker value={employee} onChange={setEmployee} />
        </div>
      </Card>

      {employee === null ? (
        <Card className="mt-3 p-8 text-center">
          <p className="text-sm text-muted">Choose an employee to see what they are paid.</p>
        </Card>
      ) : (
        <>
          {current.data !== undefined && (
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <Card className="p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Current package
                </h2>
                <p className="mt-2 text-2xl font-extrabold leading-none text-ink">
                  <Money value={current.data.basicSalary} />
                  <span className="ml-2 align-middle text-[13px] font-normal text-muted">
                    basic · {current.data.structureName}
                  </span>
                </p>
                <p className="mt-1 text-xs text-muted">
                  In force since {formatDate(current.data.effectiveFrom)}
                </p>
                {current.data.rangeWarning !== undefined && (
                  <p className="mt-2 flex items-start gap-1.5 text-[13px] text-[color:var(--sms-warning)]">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    {current.data.rangeWarning}
                  </p>
                )}
              </Card>

              {breakdown !== undefined && (
                <Card className="p-4">
                  <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                    What that comes to each month
                  </h2>
                  <dl className="mt-2 text-sm">
                    {breakdown.earnings.map((line) => (
                      <Line key={line.code} label={line.name} value={line.amount} />
                    ))}
                    <Line label="Gross" value={breakdown.grossEarnings} strong />
                    {breakdown.deductions.map((line) => (
                      <Line key={line.code} label={line.name} value={-line.amount} />
                    ))}
                    <Line label="Net pay" value={breakdown.netPay} strong />
                  </dl>
                  <p className="mt-2 text-[12px] text-muted">
                    For a whole month. A payroll run pro-rates this for anybody who joined, left or
                    took unpaid leave part way through.
                  </p>
                </Card>
              )}
            </div>
          )}

          <Card className="mt-3 overflow-hidden">
            <div className="border-b-2 border-line px-4 py-3">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Salary history
              </h2>
            </div>
            <DataTable
              columns={columns}
              rows={history.data}
              rowKey={(row) => row.id}
              loading={history.isPending}
              error={history.error}
              onRetry={() => void history.refetch()}
              emptyTitle="No salary set"
              emptyDescription="A payroll run will skip this employee until a package is assigned."
            />
          </Card>
        </>
      )}

      {assigning && employee !== null && (
        <Modal
          open
          onClose={() => setAssigning(false)}
          title={current.data === undefined ? `Set ${employee.name}'s salary` : 'Record a raise'}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              assign.mutate();
            }}
          >
            {current.data !== undefined && (
              <p className="border border-line bg-surface p-3 text-[13px] text-muted">
                The current package (<Money value={current.data.basicSalary} /> since{' '}
                {formatDate(current.data.effectiveFrom)}) is closed the day before the new one
                starts. Nothing already paid changes.
              </p>
            )}

            <SelectField
              label="Package"
              required
              value={structureId}
              onChange={(event) => setStructureId(event.target.value)}
            >
              <option value="">Choose…</option>
              {(structures.data ?? []).map((structure) => (
                <option key={structure.id} value={structure.id}>
                  {structure.name}
                </option>
              ))}
            </SelectField>

            <TextField
              label="Basic salary"
              type="number"
              step="0.01"
              min="1"
              required
              value={basic}
              onChange={(event) => setBasic(event.target.value)}
              hint="Every percentage component is worked out from this"
            />

            <TextField
              label="Takes effect from"
              type="date"
              required
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              hint="Must be after the current package started"
            />

            <TextField
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Promotion, annual review, correction…"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setAssigning(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={structureId === '' || basic === '' || assign.isPending}
              >
                {assign.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Line({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div
      className={`flex items-baseline justify-between border-b border-line py-1.5 last:border-0 ${
        strong === true ? 'font-semibold' : ''
      }`}
    >
      <dt className="text-muted">{label}</dt>
      <dd>
        <Money value={value} bold={strong} />
      </dd>
    </div>
  );
}
